import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Edit, Trash2, Check, Loader2, List } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";

interface OpportunitySourcesModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onSourceCreated?: () => void;
}

export function OpportunitySourcesModal({
  isOpen,
  onClose,
  slug,
  onSourceCreated,
}: OpportunitySourcesModalProps) {
  const { toast } = useToast();
  const [sources, setSources] = useState<any[]>([]);
  const [loading, setLoading] = useState({
    sources: false,
    create: false,
    update: false,
    delete: false,
  });
  const [editingSource, setEditingSource] = useState<any | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    is_default: false,
    is_active: true,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState<string | null>(null);

  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && slug) {
      fetchSources();
    }
  }, [isOpen, slug]);

  useEffect(() => {
    if (editingSource && nameInputRef.current) {
      setTimeout(() => {
        nameInputRef.current?.focus();
        nameInputRef.current?.select();
      }, 50);
    }
  }, [editingSource]);

  const fetchSources = async () => {
    try {
      setLoading((prev) => ({ ...prev, sources: true }));

      const response = await opportunitySourcesAPI.getSources({
        slug,
        is_active: true,
      });

      const sortedSources = response.data.sort((a, b) => {
        if (a.is_default !== b.is_default) {
          return b.is_default ? 1 : -1;
        }
        return a.name.localeCompare(b.name);
      });

      setSources(sortedSources);
    } catch (error: any) {
      toast({
        title: "Error al cargar fuentes",
        description: error.message || "No se pudieron cargar las fuentes",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, sources: false }));
    }
  };

  const handleResetForm = () => {
    setFormData({
      name: "",
      is_default: false,
      is_active: true,
    });
    setEditingSource(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Error",
        description: "El nombre es obligatorio",
        variant: "destructive",
      });
      return;
    }

    try {
      if (editingSource) {
        setLoading((prev) => ({ ...prev, update: true }));

        if (formData.is_default) {
          await opportunitySourcesAPI.setDefaultSource(editingSource.id, slug);
        } else {
          await opportunitySourcesAPI.updateSource(editingSource.id, {
            name: formData.name.trim(),
            is_default: formData.is_default,
            is_active: formData.is_active,
            slug,
          });
        }

        toast({
          title: "✅ Fuente actualizada",
          description: "La fuente se ha actualizado correctamente",
        });
      } else {
        setLoading((prev) => ({ ...prev, create: true }));

        const sourceData = {
          slug,
          name: formData.name.trim(),
          is_default: formData.is_default,
          is_active: formData.is_active,
        };

        await opportunitySourcesAPI.createSource(sourceData);

        toast({
          title: "✅ Fuente creada",
          description: "La fuente se ha creado correctamente",
        });
      }

      handleResetForm();

      setTimeout(() => {
        fetchSources();
      }, 500);

      onSourceCreated?.();
    } catch (error: any) {
      toast({
        title: editingSource ? "Error al actualizar" : "Error al crear",
        description: error.message || "Ha ocurrido un error",
        variant: "destructive",
      });

      setTimeout(() => {
        fetchSources();
      }, 500);
    } finally {
      setLoading((prev) => ({
        ...prev,
        create: false,
        update: false,
      }));
    }
  };

  const handleEdit = (source: any) => {
    setEditingSource(source);
    setFormData({
      name: source.name,
      is_default: source.is_default,
      is_active: source.is_active,
    });
  };

  const handleDeleteClick = (id: string) => {
    setSourceToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!sourceToDelete) return;

    try {
      setLoading((prev) => ({ ...prev, delete: true }));
      await opportunitySourcesAPI.deleteSource(sourceToDelete, slug);

      toast({
        title: "✅ Fuente eliminada",
        description: "La fuente se ha eliminado correctamente",
      });

      setTimeout(() => {
        fetchSources();
      }, 300);
    } catch (error: any) {
      toast({
        title: "Error al eliminar",
        description: error.message || "No se pudo eliminar la fuente",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, delete: false }));
      setDeleteDialogOpen(false);
      setSourceToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSourceToDelete(null);
  };

  const handleSetDefault = async (id: string) => {
    try {
      setLoading((prev) => ({ ...prev, update: true }));

      const result = await opportunitySourcesAPI.setDefaultSource(id, slug);

      toast({
        title: "✅ Fuente predeterminada",
        description: "La fuente se ha establecido como predeterminada",
      });

      setTimeout(() => {
        fetchSources();
      }, 300);
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.message || "No se pudo establecer como predeterminada",
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, update: false }));
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <List className="h-6 w-6" />
              Gestión de Fuentes de Cotización
            </DialogTitle>
            <DialogDescription>
              Configura las fuentes de donde provienen tus cotizaciones (ej.
              Facebook, Web, Referido).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <form
              onSubmit={handleSubmit}
              className="space-y-4 p-4 border rounded-lg"
            >
              <div>
                <Label htmlFor="name">Nombre de la fuente *</Label>
                <Input
                  id="name"
                  ref={nameInputRef}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  placeholder="Ej: Facebook, LinkedIn, Web Site..."
                  className="mt-1"
                  disabled={loading.create || loading.update}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_default}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_default: checked })
                      }
                      disabled={loading.create || loading.update}
                    />
                    <span>Establecer como predeterminada</span>
                  </Label>
                  <p className="text-sm text-gray-500">
                    Esta fuente se seleccionará por defecto al crear
                    cotizaciones
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Switch
                      checked={formData.is_active}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_active: checked })
                      }
                      disabled={loading.create || loading.update}
                    />
                    <span>Activa</span>
                  </Label>
                  <p className="text-sm text-gray-500">
                    Las fuentes inactivas no aparecerán en la lista
                  </p>
                </div>
              </div>

              <DialogFooter>
                {editingSource && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleResetForm}
                    disabled={loading.create || loading.update}
                  >
                    Cancelar edición
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={
                    !formData.name.trim() || loading.create || loading.update
                  }
                >
                  {loading.create || loading.update ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : editingSource ? (
                    <Edit className="h-4 w-4 mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {editingSource ? "Actualizar Fuente" : "Crear Fuente"}
                </Button>
              </DialogFooter>
            </form>

            <div>
              <h3 className="text-lg font-semibold mb-4">Fuentes existentes</h3>

              {loading.sources ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin" />
                </div>
              ) : sources.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No hay fuentes configuradas
                </div>
              ) : (
                <div className="space-y-3">
                  {sources.map((source) => (
                    <div
                      key={source.id}
                      className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex flex-col">
                          <span className="font-medium">{source.name}</span>
                          <div className="flex items-center gap-2 text-sm text-gray-500">
                            {source.is_default && (
                              <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                <Check className="h-3 w-3" />
                                Predeterminada
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        {!source.is_default && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSetDefault(source.id)}
                            disabled={
                              loading.update || loading.delete || loading.create
                            }
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Predeterminar
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(source)}
                          disabled={
                            loading.update || loading.delete || loading.create
                          }
                        >
                          <Edit className="h-3 w-3" />
                        </Button>

                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteClick(source.id)}
                          disabled={
                            loading.delete || loading.update || loading.create
                          }
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t pt-4">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={loading.create || loading.update || loading.delete}
            >
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={handleDeleteCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Estás seguro de eliminar esta fuente?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. La fuente se eliminará
              permanentemente y ya no estará disponible para nuevas
              cotizaciones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleDeleteCancel}
              disabled={loading.delete}
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={loading.delete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {loading.delete ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                "Eliminar"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
